import React, { HTMLProps, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMapMarkerAlt, faTimes, faTimesCircle, faUpload } from "@fortawesome/free-solid-svg-icons";
import Button, { ButtonProps } from "./Button";
import { DefaultState, FormState, useListener } from "typed-react-form";
import { Balloon, ErrorBalloon } from "./Balloon";
import { geoCode, Address, Language } from "consumerhouse-shared";
import Spinner from "./Spinner";
import { useLanguage, Translation } from "../useLanguage";

const CheckLabel = styled.label`
    display: inline-flex;
    align-items: center;
`;

const CustomCheck = styled.input`
    position: relative;
    appearance: none;
    font-size: inherit;
    outline: none;
    margin: 2px 8px;
    height: 27px;
    min-height: 27px;
    width: 27px;
    min-width: 27px;
    border-radius: 5px;
    background: #0001;
    user-select: none;

    &:after {
        position: absolute;
        top: 0;
        left: 0;
        height: 27px;
        min-height: 27px;
        width: 27px;
        min-width: 27px;
        transform: scale(0.6);
        transition: 100ms;
    }

    &:hover,
    &:active,
    &:focus {
        background: #ec454f44;
    }

    &:checked {
        background-color: #ec454f;
        color: white;
        transition: 100ms;
        animation: check 100ms;

        &:disabled {
            background: #b1a1a2;
        }
    }

    &:checked:after {
        transition: 100ms;
        content: url("data:image/svg+xml;base64,PHN2ZyBhcmlhLWhpZGRlbj0idHJ1ZSIgZm9jdXNhYmxlPSJmYWxzZSIgZGF0YS1wcmVmaXg9ImZhcyIgZGF0YS1pY29uPSJjaGVjayIgY2xhc3M9InN2Zy1pbmxpbmUtLWZhIGZhLWNoZWNrIGZhLXctMTYiIHJvbGU9ImltZyIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiB2aWV3Qm94PSIwIDAgNTEyIDUxMiI+PHBhdGggZmlsbD0id2hpdGUiIGQ9Ik0xNzMuODk4IDQzOS40MDRsLTE2Ni40LTE2Ni40Yy05Ljk5Ny05Ljk5Ny05Ljk5Ny0yNi4yMDYgMC0zNi4yMDRsMzYuMjAzLTM2LjIwNGM5Ljk5Ny05Ljk5OCAyNi4yMDctOS45OTggMzYuMjA0IDBMMTkyIDMxMi42OSA0MzIuMDk1IDcyLjU5NmM5Ljk5Ny05Ljk5NyAyNi4yMDctOS45OTcgMzYuMjA0IDBsMzYuMjAzIDM2LjIwNGM5Ljk5NyA5Ljk5NyA5Ljk5NyAyNi4yMDYgMCAzNi4yMDRsLTI5NC40IDI5NC40MDFjLTkuOTk4IDkuOTk3LTI2LjIwNyA5Ljk5Ny0zNi4yMDQtLjAwMXoiPjwvcGF0aD48L3N2Zz4=");
    }
`;

const CustomRadio = styled.input`
    position: relative;
    appearance: none;
    font-size: inherit;
    outline: none;
    margin: 2px 8px;
    height: 27px;
    min-height: 27px;
    width: 27px;
    min-width: 27px;
    border-radius: 50%;
    background: #0001;
    user-select: none;

    &:after {
        position: absolute;
        top: 0;
        left: 0;
        height: 27px;
        min-height: 27px;
        width: 27px;
        min-width: 27px;
        transform: scale(0.6);
        transition: 100ms;
    }

    &:hover,
    &:active,
    &:focus {
        background: #ec454f44;
    }

    &:checked {
        background-color: #ec454f;
        color: white;
        transition: 100ms;
        animation: check 100ms;

        &:disabled {
            background: #b1a1a2;
        }
    }

    &:checked:after {
        content: url("data:image/svg+xml;base64,PHN2ZyBhcmlhLWhpZGRlbj0idHJ1ZSIgZm9jdXNhYmxlPSJmYWxzZSIgZGF0YS1wcmVmaXg9ImZhcyIgZGF0YS1pY29uPSJjaXJjbGUiIGNsYXNzPSJzdmctaW5saW5lLS1mYSBmYS1jaXJjbGUgZmEtdy0xNiIgcm9sZT0iaW1nIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA1MTIgNTEyIj48cGF0aCBmaWxsPSJ3aGl0ZSIgZD0iTTI1NiA4QzExOSA4IDggMTE5IDggMjU2czExMSAyNDggMjQ4IDI0OCAyNDgtMTExIDI0OC0yNDhTMzkzIDggMjU2IDh6Ij48L3BhdGg+PC9zdmc+");
        transition: 100ms;
    }
`;

export type RawFormInputProps = React.InputHTMLAttributes<HTMLInputElement> & {
    className?: string;
    style?: React.CSSProperties;
    children?: React.ReactNode;
};

export const RawFormCheck = React.forwardRef<HTMLInputElement, RawFormInputProps>(({ className, style, children, ...rest }, ref) => {
    return (
        <CheckLabel className={className} style={style}>
            <CustomCheck type="checkbox" ref={ref} {...rest} />
            {children}
        </CheckLabel>
    );
});

export const RawFormRadio = React.forwardRef<HTMLInputElement, RawFormInputProps>(({ className, style, children, ...rest }, ref) => {
    return (
        <CheckLabel className={className} style={style}>
            <CustomRadio type="radio" ref={ref} {...rest} />
            {children}
        </CheckLabel>
    );
});

export const Form = styled.form`
    display: flex;
    flex-direction: column;
    align-items: stretch;
`;

// Note: can be nested
export const FormRow = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin: 0.3em 0;
    > div {
        margin: 0;
    }
    flex-grow: 1;
`;

export const FormLabel = styled.label`
    display: inline-flex;
    align-items: center;
    min-width: 160px;
    max-width: 160px;
    font-weight: bold;
`;

export const RawFormTextArea = styled.textarea<{ error?: boolean }>`
    resize: vertical;
    max-width: 100%;
    appearance: none;
    border: 2px solid ${(props) => (props.error ? "#f00" : "#0002")};
    font-family: inherit;
    font-size: inherit;
    padding: 0.5em;
    border-radius: 0.7em;
    outline: none;
    transition: 100ms;
    background: white;

    &:active,
    &:hover,
    &:focus {
        transition: 200ms;
        border: 2px solid #ec454f;
    }

    &:disabled {
        border: 2px solid transparent;
    }
`;

export const RawFormInput = styled.input<{ error?: boolean }>`
    appearance: none;
    border: 2px solid ${(props) => (props.error ? "#f00" : "#0002")};
    font-family: inherit;
    font-size: inherit;
    padding: 0.5em;
    border-radius: 0.7em;
    outline: none;
    transition: 100ms;
    background: white;

    &:active,
    &:hover,
    &:focus {
        transition: 200ms;
        border: 2px solid #ec454f;
    }

    &:disabled {
        border: 2px solid transparent;
    }
`;

const FormErrorElement = styled.p`
    color: red;
    font-weight: bold;
    text-align: right;
    margin: 0.3em 0.5em;
`;

const AddressContainer = styled.span`
    position: relative;
`;

const AddressList = styled.div`
    top: 2.5em;
    left: 0em;
    width: 100%;
    position: absolute;
    overflow: hidden;
    z-index: 20;
    border-radius: 0.5em;
    transform-origin: top center;
    animation: dropButtonIn 100ms;
    background: #eee;
    box-shadow: 2px 2px 10px #0005;
`;

const AddressElement = styled.span`
    display: inline-block;
    padding: 0.7em 1em;
    color: #4268ff;
    font-weight: bold;
    border-top: 2px solid #0001;
    cursor: pointer;
    width: 100%;

    transition: 100ms;
    &:hover {
        background: #0002;
        transition: 100ms;
    }
`;

const AddressSpinner = styled(Spinner)`
    position: absolute;
    right: 0.8em;
    top: 0.65em;
    opacity: 0.5;
`;

const AddressNoResults = styled.p`
    padding: 1em;
    color: red;
    font-weight: bold;
`;

export type RawFormLocationProps = {
    onChange?: (address: Address) => void;
    requireStreet?: boolean;
    readOnly?: boolean;
    style?: React.CSSProperties;
    className?: string;
    defaultSelected?: Address;
};

export function RawFormLocation(props: RawFormLocationProps) {
    const [state, setState] = useState<{ query: string; selected: Address | null; results: Address[] | null }>({
        query: props.defaultSelected?.full ?? "",
        selected: props.defaultSelected ?? null,
        results: null,
    });
    const language = useLanguage();

    useEffect(() => {
        async function updateLocation() {
            let addresses = await geoCode(state.query, props.requireStreet ?? false);
            setState((state) => ({
                ...state,
                results: addresses,
            }));
        }

        if (state.selected === null && state.query.length > 3) {
            let id = setTimeout(updateLocation, 500);
            return () => clearTimeout(id);
        }
    }, [state.query, props.requireStreet]);

    return (
        <AddressContainer className={props.className} style={props.style}>
            <RawFormInput
                placeholder=""
                disabled={props.readOnly}
                value={state.query}
                style={{ width: "100%" }}
                onChange={(ev) => {
                    setState({
                        query: ev.target.value,
                        selected: null,
                        results: null,
                    });
                }}
            />
            {state.results === null && state.selected === null && state.query.length > 0 && <AddressSpinner />}
            {state.results && (
                <AddressList>
                    {state.results.map((e, i) => (
                        <AddressElement
                            onClick={() => {
                                setState({
                                    query: e.full,
                                    selected: e,
                                    results: null,
                                });
                                props.onChange?.(e);
                            }}
                            key={i}>
                            <FontAwesomeIcon icon={faMapMarkerAlt} /> {e.full}
                        </AddressElement>
                    ))}
                    {state.results.length === 0 && (
                        <AddressNoResults>
                            <FontAwesomeIcon icon={faTimesCircle} /> {language("location-not-found")}
                        </AddressNoResults>
                    )}
                </AddressList>
            )}
        </AddressContainer>
    );
}

export function RawFormError({ children }: { children?: React.ReactNode }) {
    return (
        <FormErrorElement>
            <FontAwesomeIcon icon={faTimes} /> {children}
        </FormErrorElement>
    );
}

export const FormNote = styled.p`
    padding: 1em;
    font-weight: bold;
    color: #777;
    font-size: 0.9em;
    /* border: 1px solid #0002; */
`;

export const RawFormMultipleSelect = styled.select<{ error?: boolean }>`
    appearance: none;
    border: 2px solid ${(props) => (props.error ? "#f00" : "#0002")};
    font-family: inherit;
    font-size: inherit;
    padding: 0.5em 2em 0.5em 0.5em;
    border-radius: 0.7em;
    outline: none;
    transition: 100ms;
    background: white;

    &:active,
    &:hover,
    &:focus {
        transition: 200ms;
        border: 2px solid #ec454f;
    }

    &:disabled {
        border: 2px solid transparent;
    }
`;

export const RawFormSelect = styled(RawFormMultipleSelect)`
    background-image: url("data:image/svg+xml;base64,PHN2ZyBhcmlhLWhpZGRlbj0idHJ1ZSIgZm9jdXNhYmxlPSJmYWxzZSIgZGF0YS1wcmVmaXg9ImZhcyIgZGF0YS1pY29uPSJjaGV2cm9uLWRvd24iIGNsYXNzPSJzdmctaW5saW5lLS1mYSBmYS1jaGV2cm9uLWRvd24gZmEtdy0xNCIgcm9sZT0iaW1nIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA0NDggNTEyIj48cGF0aCBmaWxsPSJjdXJyZW50Q29sb3IiIGQ9Ik0yMDcuMDI5IDM4MS40NzZMMTIuNjg2IDE4Ny4xMzJjLTkuMzczLTkuMzczLTkuMzczLTI0LjU2OSAwLTMzLjk0MWwyMi42NjctMjIuNjY3YzkuMzU3LTkuMzU3IDI0LjUyMi05LjM3NSAzMy45MDEtLjA0TDIyNCAyODQuNTA1bDE1NC43NDUtMTU0LjAyMWM5LjM3OS05LjMzNSAyNC41NDQtOS4zMTcgMzMuOTAxLjA0bDIyLjY2NyAyMi42NjdjOS4zNzMgOS4zNzMgOS4zNzMgMjQuNTY5IDAgMzMuOTQxTDI0MC45NzEgMzgxLjQ3NmMtOS4zNzMgOS4zNzItMjQuNTY5IDkuMzcyLTMzLjk0MiAweiI+PC9wYXRoPjwvc3ZnPg==");
    background-repeat: no-repeat;
    background-position: right 0.7em top 50%;
    background-size: 0.65em auto;
`;

type FileInputProps = {
    onFile: (file: File) => void;
} & ButtonProps;

export function FileInput(props: FileInputProps) {
    const { onFile, ...buttonProps } = props;
    const ref = useRef<HTMLInputElement>(null);
    return (
        <>
            <Button type="button" color="#5a8ee8" onClick={() => ref.current!.click()} icon={faUpload} {...buttonProps} />
            <input
                ref={ref}
                type="file"
                hidden
                onChange={async (input) => {
                    let file = input.target.files?.[0];
                    if (file) onFile(file);
                    input.target.value = "";
                }}
            />
        </>
    );
}

export const SmallInput = styled(RawFormInput)`
    padding: 0.3em;
    border-radius: 0.4em;
    width: 160px;
    min-width: 160px;
`;

export type FormInputProps<T extends object, U> = {
    form: FormState<T>;
    name: keyof T;
    title?: React.ReactNode;
} & Omit<HTMLProps<U>, "form" | "name" | "as" | "ref">;

export function FormNumber<T extends object>(props: FormInputProps<T, HTMLInputElement>) {
    const { form, name, title, ...rest } = props;
    const { value, setValue, state, error } = useListener(form, name);
    const inputRef = useRef<HTMLInputElement>(null);
    const language = useLanguage();
    return (
        <>
            <RawFormInput
                ref={inputRef}
                disabled={state.isSubmitting}
                type="number"
                value={value as any}
                onChange={(ev) => setValue(parseFloat(ev.target.value as any) as any)}
                {...rest}
            />
            {title && (
                <Balloon onHover targetRef={inputRef}>
                    {title}
                </Balloon>
            )}
            <ErrorBalloon targetRef={inputRef} visible={!!error}>
                {error && language(error as Translation)}
            </ErrorBalloon>
        </>
    );
}

export function FormDate<T extends object>(props: FormInputProps<T, HTMLInputElement>) {
    const { form, name, title, ...rest } = props;
    const { value, setValue, error, state } = useListener(form, name);
    const inputRef = useRef<HTMLInputElement>(null);
    const language = useLanguage();
    const isSafari = !!(window as any).safari || true;

    return (
        <>
            {isSafari ? (
                <RawFormInput
                    style={{ minHeight: "2.5em", minWidth: "4em" }}
                    disabled={state.isSubmitting}
                    ref={inputRef}
                    error={!!error}
                    type="text"
                    placeholder="dd/mm/yyyy"
                    defaultValue={value ? new Date(value as any).toISOString().split("T")[0] : ""}
                    onBlur={(ev) => {
                        // Safari compat stuff
                        let split = ev.target.value.split(/[/-]/);
                        if (split.length == 3) {
                            if (split[2].length === 4) {
                                split = split.reverse();
                            }
                            let d = new Date(parseInt(split[0]), parseInt(split[1]) - 1, parseInt(split[2]));
                            if (!isNaN(d.getTime())) {
                                setValue(d.getTime() as any);
                            }
                        }
                    }}
                    {...rest}
                />
            ) : (
                <RawFormInput
                    style={{ minHeight: "2.5em", minWidth: "4em" }}
                    disabled={state.isSubmitting}
                    ref={inputRef}
                    error={!!error}
                    type="date"
                    value={value ? new Date(value as any).toISOString().split("T")[0] : ""}
                    onChange={(ev) => {
                        if (ev.target.valueAsDate) {
                            setValue(ev.target.valueAsDate.getTime() as any);
                        }
                    }}
                    {...rest}
                />
            )}
            {title && (
                <Balloon onHover targetRef={inputRef}>
                    {title}
                </Balloon>
            )}
            <ErrorBalloon targetRef={inputRef} visible={!!error}>
                {error && language(error as Translation)}
            </ErrorBalloon>
        </>
    );
}

export function FormText<T extends object>(props: FormInputProps<T, HTMLInputElement>) {
    const { form, name, title, ...rest } = props;
    const { value, setValue, error, state } = useListener(form, name);
    const inputRef = useRef<HTMLInputElement>(null);
    const language = useLanguage();
    return (
        <>
            <RawFormInput
                disabled={state.isSubmitting}
                ref={inputRef}
                error={!!error}
                type="text"
                value={(value as any) ?? ""}
                onChange={(ev) => setValue(ev.target.value as any)}
                {...rest}
            />
            {title && (
                <Balloon onHover targetRef={inputRef}>
                    {title}
                </Balloon>
            )}
            <ErrorBalloon targetRef={inputRef} visible={!!error}>
                {error && language(error as Translation)}
            </ErrorBalloon>
        </>
    );
}

export function FormCheck<T extends object>(props: FormInputProps<T, HTMLInputElement>) {
    const { form, name, title, ...rest } = props;
    const { value, setValue, state } = useListener(form, name);
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <>
            <RawFormCheck
                disabled={state.isSubmitting}
                checked={value as any}
                ref={inputRef}
                onChange={(ev) => setValue(ev.target.checked as any)}
                {...rest}
            />
            {title && (
                <Balloon onHover targetRef={inputRef}>
                    {title}
                </Balloon>
            )}
            {/* <ErrorBalloon targetRef={inputRef} visible={!!error}>
                {error}
            </ErrorBalloon> */}
        </>
    );
}

export function FormRadio<T extends object>(props: FormInputProps<T, HTMLInputElement>) {
    const { form, name, title, ...rest } = props;
    const { value, setValue, state } = useListener(form, name);
    const inputRef = useRef<HTMLInputElement>(null);
    return (
        <>
            <RawFormRadio
                disabled={state.isSubmitting}
                name={name as string}
                ref={inputRef}
                checked={(rest.value as any) === value}
                onChange={(ev) => setValue(rest.value as any)}
                {...rest}
            />
            {title && (
                <Balloon onHover targetRef={inputRef}>
                    {title}
                </Balloon>
            )}
            {/* <ErrorBalloon targetRef={inputRef} visible={!!error}>
                {error}
            </ErrorBalloon> */}
        </>
    );
}

export function FormSelect<T extends object>(props: FormInputProps<T, HTMLSelectElement>) {
    const { form, name, title, ...rest } = props;
    const { value, setValue, error, state } = useListener(form, name);
    const inputRef = useRef<HTMLSelectElement>(null);
    const language = useLanguage();
    return (
        <>
            <RawFormSelect
                disabled={state.isSubmitting}
                value={value as any}
                ref={inputRef}
                onChange={(ev) => setValue(ev.target.value as any)}
                {...rest}
            />
            {title && (
                <Balloon onHover targetRef={inputRef}>
                    {title}
                </Balloon>
            )}
            <ErrorBalloon targetRef={inputRef} visible={!!error}>
                {error && language(error as Translation)}
            </ErrorBalloon>
        </>
    );
}

export function FormTextArea<T extends object>(props: FormInputProps<T, HTMLTextAreaElement>) {
    const { form, name, title, ...rest } = props;
    const { value, setValue, error, state } = useListener(form, name);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const language = useLanguage();
    return (
        <>
            <RawFormTextArea
                disabled={state.isSubmitting}
                value={value as any}
                ref={inputRef}
                onChange={(ev) => setValue(ev.target.value as any)}
                {...rest}
            />
            {title && (
                <Balloon onHover targetRef={inputRef}>
                    {title}
                </Balloon>
            )}
            <ErrorBalloon targetRef={inputRef} visible={!!error}>
                {error && language(error as Translation)}
            </ErrorBalloon>
        </>
    );
}

export function FormError<T extends object>(props: { form: FormState<T>; name: keyof T }) {
    const { error } = useListener(props.form, props.name);
    const language = useLanguage();
    if (!error) return null;
    return <RawFormError>{language(error as Translation)}</RawFormError>;
}
